/**
 * RefineButton - FAB with dropdown menu for refinement options
 */

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useStyleStore } from '../store/useStyleStore';
import './RefineButton.css';

/** Vertical gap between the FAB and the popup. */
const POPUP_GAP = 12;
/** Minimum distance the popup keeps from every viewport edge. */
const VIEWPORT_MARGIN = 16;
/** Bounds for the popup height before it starts scrolling internally. */
const POPUP_MAX_HEIGHT = 400;
const POPUP_MIN_HEIGHT = 160;

interface PopupPosition {
  left: number;
  bottom: number;
  maxHeight: number;
}

interface RefineButtonProps {
  onRefine: () => void;
  disabled?: boolean;
  isRefining?: boolean;
  onSaveAsPrompt: () => void;
  onManagePrompts: () => void;
}

export function RefineButton({
  onRefine,
  disabled,
  isRefining,
  onSaveAsPrompt,
  onManagePrompts,
}: RefineButtonProps) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [position, setPosition] = useState<PopupPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { getAllStyles, getSelectedStyle, selectStyle } = useStyleStore();

  const allStyles = getAllStyles();
  const selectedStyle = getSelectedStyle();

  // The popup is rendered in a portal, so it is positioned against the
  // viewport from the FAB's own rect instead of against an ancestor box.
  const updatePosition = useCallback(() => {
    const anchor = containerRef.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;

    const rect = anchor.getBoundingClientRect();
    const width = menu.offsetWidth;
    const centeredLeft = rect.left + rect.width / 2 - width / 2;
    const rightmostLeft = window.innerWidth - VIEWPORT_MARGIN - width;
    const spaceAbove = rect.top - POPUP_GAP - VIEWPORT_MARGIN;

    setPosition({
      left: Math.max(VIEWPORT_MARGIN, Math.min(centeredLeft, rightmostLeft)),
      bottom: window.innerHeight - rect.top + POPUP_GAP,
      maxHeight: Math.max(POPUP_MIN_HEIGHT, Math.min(POPUP_MAX_HEIGHT, spaceAbove)),
    });
  }, []);

  // Measure before paint so the popup never flashes at an unpositioned spot,
  // and keep it anchored while the page scrolls or resizes underneath it.
  useLayoutEffect(() => {
    if (!showDropdown) return;

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showDropdown, updatePosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setShowDropdown(false);
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const handleStyleSelect = (styleId: string) => {
    selectStyle(styleId);
    setShowDropdown(false);
    onRefine();
  };

  const handleFabClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && !isRefining) {
      setShowDropdown(!showDropdown);
    }
  };

  const getFabIcon = () => {
    if (isRefining) {
      return '⏳';
    }
    return '🪄';
  };

  const getFabClass = () => {
    let baseClass = 'refine-fab';
    if (disabled) baseClass += ' disabled';
    if (isRefining) baseClass += ' refining';
    if (showDropdown) baseClass += ' active';
    return baseClass;
  };

  const menuStyle: CSSProperties = position
    ? { left: position.left, bottom: position.bottom, maxHeight: position.maxHeight }
    : { visibility: 'hidden' };

  const dropdown = (
    <div className="style-dropdown-menu" ref={menuRef} style={menuStyle}>
      {allStyles.map((style) => (
        <button
          key={style.id}
          type="button"
          onClick={() => handleStyleSelect(style.id)}
          className={`style-option ${style.id === selectedStyle.id ? 'selected' : ''}`}
        >
          <span className="style-icon">{style.isPreset ? '📋' : '✨'}</span>
          <span className="style-name">{style.name}</span>
          {style.id === selectedStyle.id && <span className="check-mark">✓</span>}
        </button>
      ))}

      <div className="dropdown-divider"></div>

      <button
        type="button"
        onClick={() => {
          setShowDropdown(false);
          onSaveAsPrompt();
        }}
        className="style-option action-option"
      >
        <span className="style-icon">💾</span>
        <span className="style-name">{t('refineButton.saveAsPrompt')}</span>
      </button>

      <button
        type="button"
        onClick={() => {
          setShowDropdown(false);
          onManagePrompts();
        }}
        className="style-option action-option"
      >
        <span className="style-icon">⚙️</span>
        <span className="style-name">{t('refineButton.managePrompts')}</span>
      </button>
    </div>
  );

  return (
    <div className="refine-button-container" ref={containerRef}>
      <button
        type="button"
        className={getFabClass()}
        onClick={handleFabClick}
        disabled={disabled || isRefining}
        aria-label={t('refineButton.selectStyle')}
        aria-expanded={showDropdown}
      >
        <span className="fab-icon">{getFabIcon()}</span>
      </button>

      {showDropdown && createPortal(dropdown, document.body)}
    </div>
  );
}
